import React, { useState, useEffect, forwardRef } from 'react';
import FormField from '../ui/FormField';
import FormHeader from '../personal-info/FormHeader';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Lock, Eye, EyeOff, AlertCircle, Shield } from '../common/IconsComponent';
import { useUser } from '../../contexts/UserContext';
import SpinnerOverlay from '../ui/SpinnerOverlay';
import Alert from '../layout/Alert';

// ─── Constants ───────────────────────────────────────────────────────────────

const FIELDS = ['currentPassword', 'newPassword', 'confirmPassword'];

const STRENGTH_LABELS = ['סיסמה חלשה מאוד', 'סיסמה חלשה', 'סיסמה בינונית', 'סיסמה חזקה', 'סיסמה חזקה מאוד'];

const STRENGTH_COLORS = {
  bar:  ['bg-red-500', 'bg-red-500', 'bg-yellow-500', 'bg-green-500', 'bg-green-500'],
  text: ['text-red-500', 'text-red-500', 'text-yellow-500', 'text-green-500', 'text-green-600'],
};

const passwordSchema = Yup.object().shape({
  currentPassword: Yup.string().required('הסיסמה הנוכחית היא שדה חובה'),
  newPassword: Yup.string()
    .min(8, 'הסיסמה חייבת להכיל לפחות 8 תווים')
    .matches(/[A-Z]/,                    'הסיסמה חייבת להכיל לפחות אות גדולה אחת')
    .matches(/[a-z]/,                    'הסיסמה חייבת להכיל לפחות אות קטנה אחת')
    .matches(/[0-9]/,                    'הסיסמה חייבת להכיל לפחות ספרה אחת')
    .matches(/[!@#$%^&*(),.?":{}|<>]/,  'הסיסמה חייבת להכיל לפחות תו מיוחד אחד')
    .required('סיסמה חדשה היא שדה חובה'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'הסיסמאות אינן תואמות')
    .required('אישור סיסמה הוא שדה חובה'),
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const calcStrength = (pw) => {
  if (!pw) return { score: 0, feedback: '' };
  let score = 0;
  if (pw.length >= 8)                        score++;
  if (pw.length >= 12)                       score++;
  if (/[A-Z]/.test(pw))                      score++;
  if (/[a-z]/.test(pw))                      score++;
  if (/[0-9]/.test(pw))                      score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(pw))    score++;
  const clamped = Math.min(4, Math.floor(score / 1.5));
  return { score: clamped, feedback: STRENGTH_LABELS[clamped] };
};

// ─── Component ───────────────────────────────────────────────────────────────

const PasswordSection = forwardRef(({ active, onSubmit }, ref) => {
  const { changeUserPassword } = useUser();

  const [show,     setShow    ] = useState({ currentPassword: false, newPassword: false, confirmPassword: false });
  const [isLoading, setIsLoading] = useState(false);
  const [strength,  setStrength ] = useState({ score: 0, feedback: '' });
  const [alert,     setAlert    ] = useState(null);

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 10000);
  };

  const formik = useFormik({
    initialValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    validationSchema: passwordSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        await changeUserPassword(values.currentPassword, values.newPassword);
        showAlert('הסיסמה עודכנה בהצלחה', 'success');
        formik.resetForm();
        onSubmit?.({ passwordUpdated: true });
      } catch (err) {
        formik.setStatus(err.message || err);
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    setStrength(calcStrength(formik.values.newPassword));
  }, [formik.values.newPassword]);

  const toggleShow = (field) => setShow((prev) => ({ ...prev, [field]: !prev[field] }));

  if (!active) return null;

  return (
    <div dir="rtl" ref={ref} id="password-section" className="scroll-mt-24">
      <div className="bg-white rounded-2xl shadow-md border border-indigo-50 p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6 relative">

        {isLoading && (
          <SpinnerOverlay
            variant="glow"
            text="נא להמתין"
            dark={false}
            blur={false}
          />
        )}

        {alert     && <Alert alert={alert} />}

        <form onSubmit={formik.handleSubmit} noValidate>
          <FormHeader
            title="עדכון סיסמה"
            buttonText="עדכון סיסמה"
            disabled={isLoading}
            type="submit"
          />

          {/* Server error */}
          {formik.status && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2.5 bg-red-50 text-red-700 rounded-xl text-sm border border-red-100">
              <AlertCircle size={15} className="flex-shrink-0" />
              <span>{formik.status}</span>
            </div>
          )}

          <div className="space-y-4 sm:space-y-5">

            {/* Current password */}
            <PasswordField
              label="סיסמה נוכחית"
              name="currentPassword"
              show={show.currentPassword}
              onToggle={() => toggleShow('currentPassword')}
              formik={formik}
              placeholder="הזן את הסיסמה הנוכחית"
            />

            {/* New password */}
            <PasswordField
              label="סיסמה חדשה"
              name="newPassword"
              show={show.newPassword}
              onToggle={() => toggleShow('newPassword')}
              formik={formik}
              placeholder="הזן סיסמה חדשה"
            />

            {/* Strength bar */}
            {formik.values.newPassword && (
              <div className="px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-xs font-medium ${STRENGTH_COLORS.text[strength.score]}`}>
                    {strength.feedback}
                  </span>
                  <span className="text-xs text-gray-500">חוזק הסיסמה</span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${STRENGTH_COLORS.bar[strength.score]}`}
                    style={{ width: `${(strength.score / 4) * 100}%` }}
                  />
                </div>
                <p className="mt-1.5 text-xs text-gray-400">
                  8 תווים לפחות, אות גדולה, אות קטנה, ספרה ותו מיוחד
                </p>
              </div>
            )}

            {/* Confirm password */}
            <PasswordField
              label="אימות סיסמה"
              name="confirmPassword"
              show={show.confirmPassword}
              onToggle={() => toggleShow('confirmPassword')}
              formik={formik}
              placeholder="הזן שוב את הסיסמה החדשה"
            />
          </div>

          {/* Security note */}
          <div className="flex items-center justify-between mt-5 sm:mt-6 p-3 sm:p-4 bg-indigo-50 rounded-xl border border-indigo-100">
            <div className="flex items-center gap-2 sm:gap-3">
              <Shield className="text-indigo-500 flex-shrink-0" size={20} />
              <div>
                <p className="text-sm font-medium text-gray-800">סיסמה מאובטחת</p>
                <p className="text-xs text-gray-500 mt-0.5">הסיסמה שלך מוגנת ומוצפנת</p>
              </div>
            </div>
            <span className="text-gray-300 text-lg tracking-widest hidden xs:block">••••••••</span>
          </div>
        </form>
      </div>
    </div>
  );
});

// ─── Sub-component ────────────────────────────────────────────────────────────

const PasswordField = ({ label, name, show, onToggle, formik, placeholder }) => (
  <div className="relative">
    <FormField
      label={label}
      name={name}
      type={show ? 'text' : 'password'}
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      placeholder={placeholder}
      icon={<Lock size={15} className="text-indigo-500" />}
      required
      error={formik.touched[name] && formik.errors[name]}
    />
    <button
      type="button"
      onClick={onToggle}
      className="absolute left-3 top-9 text-gray-400 hover:text-gray-600 transition-colors"
      aria-label={show ? 'הסתר סיסמה' : 'הצג סיסמה'}
    >
      {show ? <EyeOff size={15} /> : <Eye size={15} />}
    </button>
  </div>
);

export default PasswordSection;